import { Box, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFormikContext } from "formik";
import React from "react";
import type { PhoneType } from "../../../../clients/graphqlTypes.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { useCountries } from "../../../commons/useCountries.js";
import { usePhoneTypes } from "../../phones/usePhoneTypes.js";
import type { OnboardingFormValues } from "./mergePrefillIntoFormValues.js";
import { FIELD_VALUE_TEXT_SX, FORM_COLORS } from "./onboardingFormStyles.js";
import { buildOnboardingPhoneTypeOptions } from "./onboardingPhoneTypes.js";
import {
  OnboardingSuggestionFrame,
  type OnboardingSuggestions,
} from "./OnboardingSuggestionFrame.js";
import { OnboardingPhoneField } from "./OnboardingPhoneField.js";
import { OnboardingStreetField } from "./OnboardingStreetField.js";

/**
 * Styles shared by the fields of the form step: the design system's input
 * (48px, #F0F5FC, 16/24, .5px, #001E64), its lifted label and hint (12/16, .4px,
 * #1A4CBF) and the error state of Karo's boards.
 */
export const ONBOARDING_FIELDS_SX = {
  "& .MuiFilledInput-root": {
    minHeight: 48,
    bgcolor: FORM_COLORS.field,
    borderRadius: "4px 4px 0 0",
    "&:hover:not(.Mui-disabled)": { bgcolor: FORM_COLORS.fieldHover },
    "&.Mui-focused": { bgcolor: FORM_COLORS.field },
    "&:after": { borderBottomColor: FORM_COLORS.text },
  },
  "& .MuiFilledInput-input, & .MuiSelect-select": {
    ...FIELD_VALUE_TEXT_SX,
    "&::placeholder": { color: FORM_COLORS.link, opacity: 1 },
  },
  "& .MuiInputLabel-root": {
    letterSpacing: ".5px",
    color: FORM_COLORS.link,
    "&.Mui-focused": { color: FORM_COLORS.text },
    "&.MuiInputLabel-shrink": { letterSpacing: ".4px" },
  },
  "& .MuiFormHelperText-root": {
    m: 0,
    mt: "2px",
    p: "0 8px 0 12px",
    fontSize: 12,
    lineHeight: "16px",
    letterSpacing: ".4px",
    color: FORM_COLORS.link,
  },
  // Error state of the design: red line, darker label and hint.
  "& .MuiFilledInput-root.Mui-error:after": { borderBottomColor: "#D21C10" },
  "& .MuiInputLabel-root.Mui-error, & .MuiFormHelperText-root.Mui-error": {
    color: "#AF0900",
  },
  // The one place a required marker shows: coral, only on the label it belongs to.
  "& .MuiFormLabel-asterisk": { color: "#FA6450" },
} as const;

/** "* Required field": what the coral asterisk on a label means. */
export const RequiredFieldNote = () => {
  const { t } = useTranslate(["OrganisationProfile"]);
  return (
    <Typography
      sx={{
        fontSize: 12,
        lineHeight: 1.45,
        letterSpacing: "0.2px",
        color: FORM_COLORS.ink3,
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{ color: "#FA6450", fontWeight: 500 }}
      >
        *
      </Box>{" "}
      {t("Required field", { ns: "OrganisationProfile" })}
    </Typography>
  );
};

/**
 * A section heading, 16px/500, its subline under it, then the section's fields
 * 20px apart. A section with a required field explains the asterisk first.
 */
export const FormSection = ({
  title,
  subline,
  legal = false,
  requiredNote = false,
  children,
}: {
  title: string;
  subline?: string;
  /** The tax section: ruled off from what is above it. */
  legal?: boolean;
  /** Puts "* Required field" above the fields. */
  requiredNote?: boolean;
  children: React.ReactNode;
}) => (
  <Box
    component="section"
    sx={
      legal
        ? { borderTop: `1px solid ${FORM_COLORS.line}`, pt: "24px" }
        : undefined
    }
  >
    <Typography
      component="h2"
      sx={{
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 1.45,
        letterSpacing: "normal",
        color: FORM_COLORS.ink,
        mb: subline ? (legal ? "6px" : "6px") : "16px",
      }}
    >
      {title}
    </Typography>
    {subline && (
      <Typography
        sx={{
          fontSize: 13,
          lineHeight: 1.45,
          letterSpacing: "normal",
          color: FORM_COLORS.ink3,
          mb: legal ? "16px" : "14px",
        }}
      >
        {subline}
      </Typography>
    )}
    <Stack spacing="20px">
      {requiredNote && <RequiredFieldNote />}
      {children}
    </Stack>
  </Box>
);

/** Two fields side by side (20px / 24px apart), stacked on a narrow screen. */
export const TwoColumns = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
      gap: "20px 24px",
    }}
  >
    {children}
  </Box>
);

/** Phone type options: Landline and Mobile, plus a stored type of another kind. */
const useOnboardingPhoneTypeOptions = (storedType: PhoneType | undefined) => {
  const { t } = useTranslate(["OrganisationProfile", "TemplatesSharing"]);
  const { phoneTypeLabels } = usePhoneTypes();
  const landline = t("Landline", { ns: "OrganisationProfile" });
  // The existing "Private" / "Privat" key, for the legacy unlabelled PRIVATE type.
  const unnamedType = t("Private", { ns: "TemplatesSharing" });
  return React.useMemo(
    () =>
      buildOnboardingPhoneTypeOptions({
        storedType,
        landline,
        unnamedType,
        typeLabels: phoneTypeLabels,
      }),
    [storedType, landline, unnamedType, phoneTypeLabels]
  );
};

interface PhoneRowProps {
  phoneLabel: string;
  /** The type the organisation's phone already had; kept as an option of its own. */
  storedPhoneType: PhoneType | undefined;
  suggestions?: OnboardingSuggestions;
  disabled?: boolean;
}

/** The phone with its country code, next to the phone type. */
export const OnboardingPhoneRow = ({
  phoneLabel,
  storedPhoneType,
  suggestions,
  disabled,
}: PhoneRowProps) => {
  const { t } = useTranslate(["OrganisationProfile"]);
  const options = useOnboardingPhoneTypeOptions(storedPhoneType);
  return (
    <TwoColumns>
      <OnboardingSuggestionFrame field="phone" suggestions={suggestions}>
        <OnboardingPhoneField
          name="phones.0.number"
          label={phoneLabel}
          disabled={disabled}
        />
      </OnboardingSuggestionFrame>
      <SelectField
        name="phones.0.type"
        label={t("Phone type", { ns: "OrganisationProfile" })}
        options={options}
        disabled={disabled}
      />
    </TwoColumns>
  );
};

type AddressTextPart = "postalCode" | "city";

/** Street, postcode and city, country: the address block of both variants. */
export const OnboardingAddressFields = ({
  suggestions,
  disabled,
}: {
  suggestions?: OnboardingSuggestions;
  disabled?: boolean;
}) => {
  const { t } = useTranslate(["Global", "OrganisationProfile"]);
  const { values, setFieldValue } = useFormikContext<OnboardingFormValues>();
  const countries = useCountries();
  const countryOptions = React.useMemo(
    () =>
      countries.map(country => ({
        id: country.code,
        label: country.name,
        value: country.code,
      })),
    [countries]
  );

  // The address is typed or picked, not placed on a map, so any coordinates it
  // carried no longer describe it once a part changes. They are dropped; the
  // address is confirmed again by picking a suggestion.
  const handleAddressPartChange =
    (part: AddressTextPart) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFieldValue("address", {
        ...values.address,
        [part]: event.target.value,
        location: null,
      });

  return (
    <>
      <OnboardingSuggestionFrame field="street" suggestions={suggestions}>
        <OnboardingStreetField />
      </OnboardingSuggestionFrame>
      <TwoColumns>
        <OnboardingSuggestionFrame field="postalCode" suggestions={suggestions}>
          <TextField
            name="address.postalCode"
            label={t("Postcode", { ns: "OrganisationProfile" })}
            onChange={handleAddressPartChange("postalCode")}
            fullWidth
          />
        </OnboardingSuggestionFrame>
        <OnboardingSuggestionFrame field="city" suggestions={suggestions}>
          <TextField
            name="address.city"
            label={t("City", { ns: "OrganisationProfile" })}
            onChange={handleAddressPartChange("city")}
            fullWidth
          />
        </OnboardingSuggestionFrame>
      </TwoColumns>
      <SelectField
        name="address.countryCode"
        label={t("Country", { ns: "Global" })}
        options={countryOptions}
        disabled={disabled}
        onChange={async countryCode => {
          await setFieldValue("address", {
            ...values.address,
            countryCode: countryCode ?? "",
            location: null,
          });
        }}
      />
    </>
  );
};
